import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api from "../lib/api";
import { toast, NoticeRegion } from "../lib/notices";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function BillingSuccess() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sid = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState("");
  const attempts = useRef(0);

  useEffect(()=>{
    if (!sid) { nav("/pricing"); return; }
    if (sid.startsWith("mock_")) {
      setStatus("paid");
      setPlan("creator");
      toast.success("Demo mode: plan upgraded!");
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid"); setPlan(r.data.plan); toast.success("Payment successful!");
        } else if (r.data.payment_status === "refunded") {
          // Terminal, not a transient state to keep polling through: Stripe's
          // Checkout Session still reports "paid" for a refunded session, so
          // without this the poll would run until the 15-attempt timeout and
          // show the generic "Something went wrong" error instead of
          // explaining what actually happened.
          setStatus("refunded");
        } else if (r.data.status === "expired" || attempts.current > 15) {
          setStatus("expired");
        } else {
          setTimeout(poll, 2000);
        }
      } catch(e){
        if (attempts.current > 15) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sid, nav]);

  return (
    <div className="min-h-screen">
      <Header/>
      <main id="main" tabIndex={-1} className="max-w-xl mx-auto px-6 py-20 text-center">
        <NoticeRegion />
        {status==="checking" && (
          <>
            <Loader2 className="mx-auto animate-spin mb-6" size={48}/>
            <div className="marquee-tag mb-3">// PAYMENT</div>
            <h1 className="font-display font-black type-title tracking-tighter">Confirming your payment…</h1>
          </>
        )}
        {status==="paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-6 text-state-success" size={64}/>
            <div className="marquee-tag mb-3">// SUCCESS</div>
            <h1 className="font-display font-black type-title tracking-tighter mb-4" data-testid="billing-success-title">Welcome to {plan.toUpperCase()}.</h1>
            <p className="text-ink-muted mb-8">Your account has been upgraded. Full quota is available immediately.</p>
            <button onClick={()=>nav("/dashboard")} className="brutal-btn" data-testid="billing-success-cta">Go to Dashboard</button>
          </>
        )}
        {status==="refunded" && (
          <>
            <div className="marquee-tag mb-3 bg-brand text-ink-on-accent">// REFUNDED</div>
            <h1 className="font-display font-black type-title tracking-tighter mb-4">This payment was refunded.</h1>
            <button onClick={()=>nav("/pricing")} className="brutal-btn brutal-btn-secondary">Back to Pricing</button>
          </>
        )}
        {(status==="expired" || status==="error") && (
          <>
            <div className="marquee-tag mb-3 bg-brand text-ink-on-accent">// ERROR</div>
            <h1 className="font-display font-black type-title tracking-tighter mb-4">Something went wrong.</h1>
            <button onClick={()=>nav("/pricing")} className="brutal-btn brutal-btn-secondary">Back to Pricing</button>
          </>
        )}
      </main>
    </div>
  );
}
